import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CrossPostView from './CrossPostView';
import { useUser } from '../providers/UserProvider';
import { Button } from '@/components/ui/Button';
import { Library, Share2, ArrowRight, Layers } from 'lucide-react';
import MultiCrossPostWizard from './MultiCrossPostWizard';

export default function CrossPostPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, reloadUser } = useUser();
  const selectedVideos = location.state?.selectedVideos || [];
  const isBatchMode = location.state?.batchMode || false;

  if (isBatchMode) {
    return (
      <div className="flex-1 overflow-auto bg-gradient-to-b from-gray-50 to-gray-100">
        <MultiCrossPostWizard 
          initialVideos={selectedVideos}
          onComplete={(result) => {
            if (result.success) {
              console.log(`Batch ID: ${result.batchId}`);
            } else {
              console.error(result.error);
            }
            navigate('/library');
          }}
        />
      </div>
    );
  }

  if (selectedVideos.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
        <div className="container mx-auto px-8 py-8 pt-16">
          {/* Hero Section */}
          <div className="mb-8 bg-white rounded-2xl p-8 shadow-lg border border-indigo-50">
            <div className="flex flex-col justify-center space-y-6">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 text-transparent bg-clip-text">
                Cross-Post Content
              </h1>
              <p className="text-gray-600 text-lg">
                Share your content across multiple platforms to maximize your reach
              </p>
              <div className="flex gap-4">
                <Button
                  onClick={() => navigate('/cross-post', { 
                    state: { 
                      batchMode: true,
                      selectedVideos: []
                    }
                  })}
                  className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 px-6 rounded-lg shadow-md hover:from-purple-600 hover:to-pink-600"
                >
                  <Layers className="h-5 w-5 mr-2" />
                  Start Multi-Cross Post Wizard
                </Button>
                <Button
                  onClick={() => navigate('/library')}
                  className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white shadow-lg shadow-indigo-500/25 rounded-xl"
                >
                  <Library className="h-5 w-5 mr-2" />
                  Go to Library
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
              </div>
            </div>
          </div>

          <div className="grid gap-8">
            {/* How it works section */}
            <div className="bg-white rounded-2xl shadow-lg border border-indigo-50 p-8">
              <div className="mb-8 inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 text-white">
                <Share2 className="h-8 w-8" />
              </div>
              
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">
                How it works:
              </h2>

              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-purple-600 font-medium">1</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900 mb-1">Select Content</h3>
                    <p className="text-gray-600">Go to your video library and select the content you want to share</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-purple-600 font-medium">2</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900 mb-1">Choose Platform</h3>
                    <p className="text-gray-600">Choose your target platform (Instagram or TikTok)</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-purple-600 font-medium">3</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900 mb-1">Schedule Posts</h3>
                    <p className="text-gray-600">Schedule your posts for optimal engagement times</p>
                  </div>
                </div>
              </div>

              <Button
                onClick={() => navigate('/')}
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg shadow-purple-500/25 rounded-xl"
              >
                <Library className="h-5 w-5 mr-2" />
                Go to Library
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </div>

            {/* Benefits section */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-8 border border-indigo-100 shadow-lg">
              <h3 className="text-xl font-semibold text-indigo-900 mb-6">
                Why cross-post your content?
              </h3>
              <div className="grid gap-6 md:grid-cols-3">
                <div className="bg-white/50 backdrop-blur-sm rounded-xl p-6 border border-indigo-100">
                  <div className="mb-4 inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 text-white">
                    <Share2 className="h-6 w-6" />
                  </div>
                  <h4 className="text-lg font-medium text-indigo-900 mb-2">Wider Reach</h4>
                  <p className="text-indigo-800">Reach a wider audience across different platforms</p>
                </div>
                <div className="bg-white/50 backdrop-blur-sm rounded-xl p-6 border border-indigo-100">
                  <div className="mb-4 inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 text-white">
                    <Share2 className="h-6 w-6" />
                  </div>
                  <h4 className="text-lg font-medium text-indigo-900 mb-2">Save Time</h4>
                  <p className="text-indigo-800">Save time by managing all your content in one place</p>
                </div>
                <div className="bg-white/50 backdrop-blur-sm rounded-xl p-6 border border-indigo-100">
                  <div className="mb-4 inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 text-white">
                    <Share2 className="h-6 w-6" />
                  </div>
                  <h4 className="text-lg font-medium text-indigo-900 mb-2">Optimize Content</h4>
                  <p className="text-indigo-800">Optimize your content for each platform automatically</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto bg-gradient-to-b from-gray-50 to-gray-100">
      <CrossPostView
        video={selectedVideos}
        user={user}
        reloadUser={reloadUser}
        onClose={(result) => {
          if (result?.type === 'success' && result?.message) {
            console.log(result.message);
          }
          window.history.back();
        }}
        onError={(error) => {
          console.error(error);
        }}
      />
    </div>
  );
} 